import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import autoMergeLevel2 from "redux-persist/es/stateReconciler/autoMergeLevel2";
import storage from "redux-persist/es/storage";
import { REHYDRATE } from "redux-persist";

export type UiStateType = {
  sideNavExpanded: boolean;
  impersonationBannerCollapsed: boolean;
  expandedCategories: string[];
};

export const initialState: UiStateType = {
  sideNavExpanded: false,
  impersonationBannerCollapsed: false,
  expandedCategories: [],
};

/**
 * IMPORTANT: Valid category IDs that can be persisted to localStorage.
 *
 * When adding new navigation categories to the desktop sidebar, you MUST update this array
 * to allow their expanded state to persist. Categories not in this list will be filtered out
 * during rehydration.
 *
 * Current valid IDs:
 * - "performanceIncome": Performance & Income category
 * - "groupManagement": Group Management category
 *
 * To add a new category:
 * 1. Add the category ID string to this array
 * 2. Add the category to SideNavigationPanel component
 * 3. Pass categoryId prop to CategoryAccordion using the constant from this array
 */
export const VALID_CATEGORY_IDS = ["performanceIncome", "groupManagement"] as const;

export type CategoryId = (typeof VALID_CATEGORY_IDS)[number];

const { actions, reducer: uiStateReducer } = createSlice({
  name: "uiState",
  initialState,
  reducers: {
    setSideNavExpanded: (state, action: PayloadAction<boolean>) => {
      state.sideNavExpanded = action.payload;
    },
    setImpersonationBannerCollapsed: (state, action: PayloadAction<boolean>) => {
      state.impersonationBannerCollapsed = action.payload;
    },
    /**
     * Toggles the expanded state of a navigation category (e.g., "performanceIncome", "groupManagement").
     * The expanded state is persisted to localStorage and automatically restored on app initialization.
     *
     * @param categoryId - The unique identifier for the category to toggle
     */
    toggleCategoryExpanded: (state, action: PayloadAction<string>) => {
      const categoryId = action.payload;
      if (state.expandedCategories.includes(categoryId)) {
        state.expandedCategories = [];
      } else {
        state.expandedCategories = [categoryId];
      }
    },
    /**
     * Sets the expanded state of a navigation category explicitly (idempotent).
     * Use this when you need to ensure a specific state rather than toggling.
     * The expanded state is persisted to localStorage and automatically restored on app initialization.
     *
     * @param payload - Object with categoryId and expanded boolean
     */
    setCategoryExpanded: (state, action: PayloadAction<{ categoryId: string; expanded: boolean }>) => {
      const { categoryId, expanded } = action.payload;
      const isCurrentlyExpanded = state.expandedCategories.includes(categoryId);

      if (expanded && !isCurrentlyExpanded) {
        // Expand: add to array if not already present
        state.expandedCategories.push(categoryId);
      } else if (!expanded && isCurrentlyExpanded) {
        // Collapse: remove from array if present
        state.expandedCategories = state.expandedCategories.filter((id) => id !== categoryId);
      }
      // If already in desired state, do nothing (idempotent)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(REHYDRATE, (state, action) => {
      // Only handle rehydration for this slice's key ("uiState")
      // Note: redux-persist shapes REHYDRATE as { key, payload: <sliceState> }
      const rehydrateAction = action as typeof action & { key?: string; payload?: UiStateType };
      if (rehydrateAction.key !== "uiState") {
        return;
      }

      // Validate rehydrated expandedCategories to ensure only valid category IDs are restored
      const rehydratedCategories = rehydrateAction.payload?.expandedCategories;

      if (Array.isArray(rehydratedCategories)) {
        state.expandedCategories = rehydratedCategories.filter((id: string) =>
          (VALID_CATEGORY_IDS as readonly string[]).includes(id),
        );
      } else {
        // Reset to empty array if rehydrated value is not an array
        state.expandedCategories = [];
      }
    });
  },
});

export const { setSideNavExpanded, setImpersonationBannerCollapsed, toggleCategoryExpanded, setCategoryExpanded } =
  actions;

// Redux-persist configuration
// Persists navigation UI preferences (sidebar state, expanded categories) to localStorage
const uiStatePersistConfig = {
  key: "uiState",
  storage: storage, // localStorage for cross-session persistence
  stateReconciler: autoMergeLevel2,
};

export default persistReducer<UiStateType>(uiStatePersistConfig, uiStateReducer);
